import { useCallback, useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { getMyStats, getMyTransactions, type MeStats, type Transaction } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { EASE_OUT } from '../../../shared/motion.ts'
import { LoadNotice } from '../components/LoadNotice'
import { gameLabel } from '../gameLabels'

export function Record() {
  const { t } = useTranslation()
  const { token } = useAuth()
  const [stats, setStats] = useState<MeStats | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [transactionsLoaded, setTransactionsLoaded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const mounted = useRef(true)
  const loadVersion = useRef(0)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!token) return
    const version = ++loadVersion.current
    setLoading(true)
    setLoadError('')
    const [statsResult, transactionsResult] = await Promise.allSettled([
      getMyStats(token),
      getMyTransactions(token, 20),
    ])
    if (!mounted.current || version !== loadVersion.current) return

    if (statsResult.status === 'fulfilled') setStats(statsResult.value)
    if (transactionsResult.status === 'fulfilled') {
      setTransactions(transactionsResult.value.transactions ?? [])
      setTransactionsLoaded(true)
    }
    if (statsResult.status === 'rejected' || transactionsResult.status === 'rejected') {
      setLoadError(t('record.loadError'))
    }
    setLoading(false)
  }, [t, token])

  useEffect(() => {
    void load()
  }, [load])

  const metrics = [
    ['netProfit', stats?.net_profit ?? null],
    ['totalWagered', stats?.total_wagered ?? null],
    ['totalWon', stats?.total_won ?? null],
    ['gamesPlayed', stats?.games_played ?? null],
    ['biggestWin', stats?.biggest_win ?? null],
  ] as const

  return (
    <div className="page page-record">
      <header className="page-heading">
        <span className="page-kicker">ATLAS LOG</span>
        <h2>{t('record.title')}</h2>
        <p>{t('record.subtitle')}</p>
      </header>

      {loadError ? (
        <LoadNotice message={loadError} onRetry={() => void load()} busy={loading} />
      ) : null}

      <section className="atlas-panel stats-grid record-stats" aria-busy={loading}>
        {metrics.map(([key, value], index) => (
          <motion.div
            className="metric-card"
            key={key}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, duration: 0.2, ease: EASE_OUT }}
          >
            <span>{t(`profile.${key}`)}</span>
            <strong className={key === 'netProfit' && value !== null ? (value < 0 ? 'bad' : value > 0 ? 'good' : '') : ''}>
              {value === null ? '—' : key === 'netProfit' && value > 0 ? `+${value}` : value}
            </strong>
          </motion.div>
        ))}
      </section>

      <h3 className="section-title">{t('record.recent')}</h3>
      <section className="atlas-panel record-list">
        {transactions.length === 0 ? (
          <p className="hint">
            {loading
              ? t('app.loading')
              : transactionsLoaded
                ? t('profile.noTx')
                : t('record.dataUnavailable')}
          </p>
        ) : (
          transactions.map((tx, index) => (
            <motion.div
              className="record-row"
              key={tx.id}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: Math.min(index * 0.025, 0.25), duration: 0.2 }}
            >
              <span>
                <strong>{tx.game_type ? gameLabel(t, tx.game_type) : t('record.platform')}</strong>
                <small>{new Date(tx.created_at).toLocaleString()}</small>
              </span>
              <b className={tx.amount < 0 ? 'bad' : tx.amount > 0 ? 'good' : ''}>
                {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
              </b>
            </motion.div>
          ))
        )}
      </section>
    </div>
  )
}
